import React, { useEffect, useState } from "react";
import { Avatar, Button, Form, Input, Progress, UploadFile } from "antd";
import TextArea from "antd/es/input/TextArea";
import Dragger from "antd/es/upload/Dragger";
import DaggerCard from "@/components/Cards/DaggerCard/DaggerCard";
import { toast } from "sonner";
import { RcFile, UploadProps } from "antd/es/upload";
import { uploadUrl } from "@/config/urls";
import { green, red } from "@ant-design/colors";
import { AddressAutofill } from "@mapbox/search-js-react";

interface CoverageAreasProps {
  onFormFinish?: (values: any) => void;
  initialValues?: any;
  onNext?: () => void;
  onPrevious?: () => void;
  isLastStep?: boolean;
}

interface CoverageAddress {
  addressLine1: string;
  addressLine2?: string;
  postalCode: string;
}

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN ?? "";

const CoverageAreas = ({
  initialValues,
  onNext,
  onPrevious,
  isLastStep,
  onFormFinish,
}: CoverageAreasProps) => {
  const [form] = Form.useForm();
  const [areas, setAreas] = useState<CoverageAddress[]>([]);

  // Warn if Mapbox token is missing
  useEffect(() => {
    console.log("🔍 Checking Mapbox token...");
    console.log(
      "Token value:",
      MAPBOX_TOKEN ? `${MAPBOX_TOKEN.substring(0, 15)}...` : "MISSING",
    );
    console.log("Token length:", MAPBOX_TOKEN?.length || 0);

    if (!MAPBOX_TOKEN) {
      console.warn(
        "⚠️ REACT_APP_MAPBOX_TOKEN is missing. Address search will not work.",
      );
      toast.error("Address search is not configured. Please contact support.");
    }
  }, []);
  const [currentAddress, setCurrentAddress] = useState<{
    addressLine1: string;
    addressLine2: string;
    postalCode: string;
  }>({
    addressLine1: "",
    addressLine2: "",
    postalCode: "",
  });

  useEffect(() => {
    if (
      initialValues &&
      initialValues.coverageArea &&
      initialValues.coverageArea.length > 0
    ) {
      setAreas(initialValues.coverageArea);
    } else {
      setAreas([]);
      form.resetFields(["fullAddress"]);
      setCurrentAddress({
        addressLine1: "",
        addressLine2: "",
        postalCode: "",
      });
    }
  }, [initialValues, form]);

  const onFinish = () => {
    // Combine addresses into a single string separated by semicolons
    const combinedAddresses = areas
      .map((area) =>
        [area.addressLine1, area.addressLine2, area.postalCode]
          .filter(Boolean)
          .join(", "),
      )
      .join("; ");

    // Check if areas exist before submitting
    if (onFormFinish) {
      onFormFinish(combinedAddresses);
    }
    form.resetFields();
    setAreas([]);
    setCurrentAddress({
      addressLine1: "",
      addressLine2: "",
      postalCode: "",
    });
  };

  const handleSave = () => {
    // Validate that at least one coverage area has been added
    if (areas.length === 0) {
      toast.error("Please add at least one coverage area before proceeding");
      return;
    }

    toast.success("Saved successfully");
    if (onFormFinish) {
      onFormFinish(areas);
      console.log(areas);
    }
  };

  const handleAddressRetrieve = (res: any) => {
    try {
      // Log the response for debugging
      console.log("Mapbox address response:", res);

      const feature = res.features?.[0];
      if (!feature) {
        console.warn("No features found in Mapbox response");
        toast.error("No address found. Please try a different search.");
        return;
      }

      // Multiple fallback methods for address extraction
      const fullAddress =
        feature.place_name ||
        feature.properties?.full_address ||
        [feature.text, feature.address].filter(Boolean).join(" ") ||
        "";

      if (!fullAddress) {
        console.warn("Could not extract address from feature:", feature);
        toast.error(
          "Address format not recognized. Please select another suggestion.",
        );
        return;
      }

      // Extract postal code with multiple fallbacks
      const postalCode =
        feature.context?.find(
          (ctx: any) => ctx.id.includes("postcode") || ctx.id.includes("zip"),
        )?.text ||
        feature.properties?.postcode ||
        "";

      // Extract address line 1 (first part before comma or entire string)
      const addressLine1 = fullAddress.split(",")[0]?.trim() || fullAddress;

      console.log("Extracted address:", { addressLine1, postalCode });

      // Update form and state
      form.setFieldsValue({ fullAddress });
      setCurrentAddress({
        addressLine1,
        addressLine2: "",
        postalCode,
      });
    } catch (error) {
      console.error("Address processing error:", error);
      toast.error("Error processing address. Please try again.");
    }
  };

  const handleAddAddress = () => {
    const formAddress = form.getFieldValue("fullAddress");

    if (!formAddress) {
      toast.error(
        "Street name not allowed. Please enter a complete address (e.g., 123 West Street) and select from the suggestions",
      );
      return;
    }

    // If we have form value but state wasn't updated, use the raw value
    if (!currentAddress.addressLine1) {
      setCurrentAddress({
        addressLine1: formAddress,
        addressLine2: "",
        postalCode: "",
      });
    }

    // Final validation
    if (!currentAddress.addressLine1) {
      toast.error(
        "Please enter a complete address (e.g., 123 West Street, City) and select from the dropdown suggestions",
      );
      return;
    }

    const newAddress: CoverageAddress = {
      addressLine1: currentAddress.addressLine1,
      addressLine2: currentAddress.addressLine2 || "",
      postalCode: currentAddress.postalCode,
    };

    // Check for duplicates
    if (areas.some((a) => a.addressLine1 === newAddress.addressLine1)) {
      toast.error("Address already exists in your coverage areas");
      return;
    }

    setAreas((prev) => [...prev, newAddress]);
    form.resetFields(["fullAddress"]);
    setCurrentAddress({
      addressLine1: "",
      addressLine2: "",
      postalCode: "",
    });
  };
  return (
    <div className="flex w-full h-full">
      <Form form={form} onFinish={onFinish} layout="vertical">
        <div className="flex flex-col w-full">
          <p className="text-sm">Step 3: Coverage Areas</p>
          <Progress
            steps={6}
            size={[80, 10]}
            strokeColor={["#1757a2", "#1757a2", "#1757a2"]}
            percent={50}
            showInfo={false}
            className="rounded-full"
            strokeLinecap="round"
            strokeWidth={1}
            style={{
              height: "6px",
              borderRadius: "999px",
            }}
          />
        </div>
        <div className="flex flex-col w-full mt-3">
          <p className="text-sm">Coverage Areas</p>

          {/* Hidden input to store fullAddress */}
          <Form.Item
            name="fullAddress"
            rules={[{ required: true, message: "Please enter an address" }]}
            noStyle
          >
            <Input type="hidden" />
          </Form.Item>

          {/* AddressAutofill for user interaction */}
          <AddressAutofill
            accessToken={MAPBOX_TOKEN}
            browserAutofillEnabled={true}
            onRetrieve={handleAddressRetrieve}
          >
            <input
              type="text"
              placeholder="Enter complete address (e.g., 123 West Street, City) and select from suggestions"
              className={`w-full p-2 border rounded ${
                !currentAddress.addressLine1 &&
                form.getFieldValue("fullAddress")
                  ? "border-yellow-500"
                  : ""
              }`}
            />
          </AddressAutofill>

          <div className="mb-4">
            {currentAddress.addressLine1 && (
              <div className="p-3 mb-3 bg-gray-50 border rounded-md">
                <p className="font-medium">Selected Address:</p>
                <p>{currentAddress.addressLine1}</p>
                {currentAddress.postalCode && (
                  <p>Postal Code: {currentAddress.postalCode}</p>
                )}
              </div>
            )}
          </div>

          <Button
            type="primary"
            onClick={handleAddAddress}
            className="mb-4 bg-[#1757a2]"
          >
            Add Coverage Area
          </Button>

          {areas.length > 0 && (
            <div className="mb-4 border p-4 rounded-md">
              <p className="font-medium mb-2">Added Coverage Areas:</p>
              {areas.map((area, index) => (
                <div key={index} className="mb-3 pb-3 border-b last:border-b-0">
                  <div className="flex justify-between">
                    <div>
                      <p>
                        {[area.addressLine1, area.addressLine2, area.postalCode]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </div>
                    <Button
                      type="text"
                      danger
                      onClick={() => {
                        const newAreas = [...areas];
                        newAreas.splice(index, 1);
                        setAreas(newAreas);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex w-full">
          <Form.Item className="w-12 pt-2 mb-1">
            <div className="flex w-full gap-2 col-12 justify-content-end align-content-center">
              <Button
                type="primary"
                ghost
                onClick={onPrevious}
                size="large"
                className="w-3"
              >
                Back
              </Button>
              <Button
                type="primary"
                onClick={handleSave}
                size="large"
                className="w-3 bg-[#1757a2] shadow-none"
              >
                Next
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default CoverageAreas;
